import React, { useState } from 'react';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import { useAuth } from '@futuredev/shared-auth';
import api from '@futuredev/shared-auth/api';
import { useTheme } from '../../contexts/ThemeContext';
import BackLink from '../../components/BackLink';
import {
  SparklesIcon,
  BuildingOfficeIcon,
  PaintBrushIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  GlobeAltIcon,
  SwatchIcon,
  Cog6ToothIcon,
  EyeIcon,
  PencilIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';

interface SiteData {
  name: string;
  type: string;
  industry: string;
  description: string;
  colorScheme: string;
  style: string;
  pages: string[];
  features: string[];
}

interface GeneratedSite {
  html: string;
  css: string;
  siteName: string;
  siteType: string;
  siteIndustry: string;
  colorScheme: string;
  style: string;
  features: string[];
}

interface SiteType {
  id: string;
  name: string;
  icon: string;
  description: string;
}

interface ColorScheme {
  id: string;
  name: string;
  colors: string[];
  gradient: string;
}

interface DesignStyle {
  id: string;
  name: string;
  description: string;
}

interface AvailableFeature {
  id: string;
  name: string;
  description: string;
}

interface StepInfo {
  num: number;
  name: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;
}

const siteTypes: SiteType[] = [
  { id: 'business', name: 'Business', icon: '🏢', description: 'Professional company website' },
  { id: 'portfolio', name: 'Portfolio', icon: '🎨', description: 'Showcase your work' },
  { id: 'landing', name: 'Landing Page', icon: '🚀', description: 'Single page conversion focused' },
  { id: 'blog', name: 'Blog', icon: '📝', description: 'Content-focused blog site' },
  { id: 'ecommerce', name: 'E-commerce', icon: '🛒', description: 'Online store' },
  { id: 'restaurant', name: 'Restaurant', icon: '🍽️', description: 'Food & dining business' },
  { id: 'agency', name: 'Agency', icon: '💼', description: 'Creative or marketing agency' },
  { id: 'nonprofit', name: 'Non-profit', icon: '❤️', description: 'Charity or community org' },
];

const industries: string[] = [
  'Technology', 'Healthcare', 'Finance', 'Education', 'Real Estate',
  'Legal', 'Construction', 'Retail', 'Food & Beverage', 'Fitness',
  'Beauty & Wellness', 'Art & Design', 'Photography', 'Music',
  'Consulting', 'Manufacturing', 'Travel', 'Entertainment', 'Other'
];

const colorSchemes: ColorScheme[] = [
  { id: 'blue', name: 'Ocean Blue', colors: ['#3B82F6', '#1E40AF', '#60A5FA'], gradient: 'from-blue-600 to-blue-800' },
  { id: 'purple', name: 'Royal Purple', colors: ['#8B5CF6', '#5B21B6', '#A78BFA'], gradient: 'from-purple-600 to-purple-800' },
  { id: 'green', name: 'Forest Green', colors: ['#10B981', '#047857', '#34D399'], gradient: 'from-green-600 to-green-800' },
  { id: 'red', name: 'Ruby Red', colors: ['#EF4444', '#B91C1C', '#F87171'], gradient: 'from-red-600 to-red-800' },
  { id: 'orange', name: 'Sunset Orange', colors: ['#F97316', '#C2410C', '#FB923C'], gradient: 'from-orange-600 to-orange-800' },
  { id: 'pink', name: 'Rose Pink', colors: ['#EC4899', '#BE185D', '#F472B6'], gradient: 'from-pink-600 to-pink-800' },
  { id: 'teal', name: 'Aqua Teal', colors: ['#14B8A6', '#0D9488', '#2DD4BF'], gradient: 'from-teal-600 to-teal-800' },
  { id: 'gray', name: 'Elegant Gray', colors: ['#6B7280', '#374151', '#9CA3AF'], gradient: 'from-gray-600 to-gray-800' },
];

const designStyles: DesignStyle[] = [
  { id: 'modern', name: 'Modern', description: 'Clean lines, bold typography, lots of whitespace' },
  { id: 'minimal', name: 'Minimalist', description: 'Simple, focused, essential elements only' },
  { id: 'bold', name: 'Bold & Creative', description: 'Eye-catching colors, unique layouts' },
  { id: 'classic', name: 'Classic', description: 'Traditional, professional, timeless' },
  { id: 'playful', name: 'Playful', description: 'Fun, colorful, animated elements' },
  { id: 'elegant', name: 'Elegant', description: 'Sophisticated, luxury feel' },
];

const availableFeatures: AvailableFeature[] = [
  { id: 'hero', name: 'Hero Section', description: 'Eye-catching header' },
  { id: 'about', name: 'About Section', description: 'Company information' },
  { id: 'services', name: 'Services', description: 'What you offer' },
  { id: 'team', name: 'Team', description: 'Meet the team' },
  { id: 'testimonials', name: 'Testimonials', description: 'Customer reviews' },
  { id: 'gallery', name: 'Gallery', description: 'Image showcase' },
  { id: 'pricing', name: 'Pricing', description: 'Pricing plans' },
  { id: 'faq', name: 'FAQ', description: 'Common questions' },
  { id: 'contact', name: 'Contact Form', description: 'Get in touch' },
  { id: 'newsletter', name: 'Newsletter', description: 'Email signup' },
  { id: 'cta', name: 'Call to Action', description: 'Conversion section' },
  { id: 'footer', name: 'Footer', description: 'Site footer' },
];

type PrefixMap = Record<string, string[]>;
type SuffixMap = Record<string, string[]>;

function AISiteBuilder(): React.ReactElement {
  const navigate: NavigateFunction = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedSite, setGeneratedSite] = useState<GeneratedSite | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [siteData, setSiteData] = useState<SiteData>({
    name: '',
    type: '',
    industry: '',
    description: '',
    colorScheme: 'blue',
    style: 'modern',
    pages: ['home'],
    features: [],
  });

  const [suggestedNames, setSuggestedNames] = useState<string[]>([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState<boolean>(false);

  const generateNameSuggestions = (type: string, industry: string): string[] => {
    const prefixes: PrefixMap = {
      business: ['Apex', 'Prime', 'Elite', 'Core', 'Peak', 'Nexus', 'Vertex', 'Catalyst', 'Pinnacle', 'Summit'],
      portfolio: ['Creative', 'Studio', 'Artisan', 'Craft', 'Vision', 'Canvas', 'Palette', 'Muse', 'Inspire', 'Design'],
      landing: ['Launch', 'Spark', 'Ignite', 'Boost', 'Elevate', 'Ascend', 'Propel', 'Surge', 'Swift', 'Rapid'],
      blog: ['Insight', 'Voice', 'Echo', 'Chronicle', 'Journal', 'Narrative', 'Story', 'Tales', 'Thoughts', 'Musings'],
      ecommerce: ['Market', 'Shop', 'Store', 'Bazaar', 'Trade', 'Cart', 'Commerce', 'Retail', 'Goods', 'Emporium'],
      restaurant: ['Taste', 'Flavor', 'Kitchen', 'Table', 'Dish', 'Cuisine', 'Bistro', 'Grill', 'Feast', 'Savory'],
      agency: ['Creative', 'Digital', 'Bold', 'Spark', 'Edge', 'Wave', 'Pixel', 'Fusion', 'Nova', 'Orbit'],
      nonprofit: ['Hope', 'Unity', 'Heart', 'Care', 'Together', 'Impact', 'Mission', 'Purpose', 'Change', 'Vision'],
    };

    const industrySuffixes: SuffixMap = {
      'Technology': ['Tech', 'Labs', 'Systems', 'Digital', 'Solutions'],
      'Healthcare': ['Health', 'Care', 'Wellness', 'Medical', 'Life'],
      'Finance': ['Capital', 'Financial', 'Wealth', 'Trust', 'Partners'],
      'Education': ['Academy', 'Learning', 'Institute', 'Education', 'Knowledge'],
      'Real Estate': ['Properties', 'Realty', 'Estates', 'Homes', 'Living'],
      'Legal': ['Law', 'Legal', 'Justice', 'Counsel', 'Associates'],
      'Construction': ['Build', 'Construction', 'Builders', 'Works', 'Structures'],
      'Retail': ['Retail', 'Goods', 'Supply', 'Products', 'Essentials'],
      'Food & Beverage': ['Foods', 'Eats', 'Bites', 'Delights', 'Tastes'],
      'Fitness': ['Fit', 'Fitness', 'Active', 'Strong', 'Wellness'],
      'Beauty & Wellness': ['Beauty', 'Glow', 'Radiance', 'Spa', 'Bliss'],
      'Art & Design': ['Studio', 'Creative', 'Design', 'Art', 'Works'],
      'Photography': ['Photo', 'Lens', 'Studio', 'Captures', 'Moments'],
      'Music': ['Sound', 'Audio', 'Music', 'Beats', 'Rhythm'],
      'Consulting': ['Consulting', 'Advisory', 'Partners', 'Group', 'Associates'],
      'Manufacturing': ['Industries', 'Manufacturing', 'Works', 'Production', 'Factory'],
      'Travel': ['Travel', 'Tours', 'Adventures', 'Journeys', 'Voyages'],
      'Entertainment': ['Entertainment', 'Media', 'Productions', 'Studios', 'Shows'],
      'Other': ['Co', 'Group', 'Hub', 'Central', 'Pro'],
    };

    const typePrefix = prefixes[type] || prefixes.business;
    const industrySuffix = industrySuffixes[industry] || industrySuffixes.Other;

    const suggestions: string[] = [];
    const usedCombos = new Set<string>();

    while (suggestions.length < 6 && suggestions.length < typePrefix.length) {
      const prefix = typePrefix[Math.floor(Math.random() * typePrefix.length)];
      const suffix = industrySuffix[Math.floor(Math.random() * industrySuffix.length)];
      const combo = `${prefix} ${suffix}`;

      if (!usedCombos.has(combo)) {
        usedCombos.add(combo);
        suggestions.push(combo);
      }
    }

    return suggestions;
  };

  const updateSiteData = (field: keyof SiteData, value: string | string[]): void => {
    setSiteData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleFeature = (featureId: string): void => {
    setSiteData((prev) => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter((f) => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 1: return !!siteData.type;
      case 2: return !!siteData.industry;
      case 3: return !!siteData.colorScheme && !!siteData.style;
      case 4: return siteData.features.length > 0;
      default: return true;
    }
  };

  const handleTypeChange = (type: string): void => {
    updateSiteData('type', type);
    if (siteData.industry) {
      const names = generateNameSuggestions(type, siteData.industry);
      setSuggestedNames(names);
      setShowNameSuggestions(!siteData.name.trim());
    }
  };

  const handleIndustryChange = (industry: string): void => {
    updateSiteData('industry', industry);
    if (siteData.type) {
      const names = generateNameSuggestions(siteData.type, industry);
      setSuggestedNames(names);
      setShowNameSuggestions(!siteData.name.trim());
    }
  };

  const selectSuggestedName = (name: string): void => {
    updateSiteData('name', name);
    setShowNameSuggestions(false);
  };

  const refreshSuggestions = (): void => {
    if (siteData.type && siteData.industry) {
      const names = generateNameSuggestions(siteData.type, siteData.industry);
      setSuggestedNames(names);
    }
  };

  const generateSite = async (): Promise<void> => {
    setIsGenerating(true);
    setError(null);

    let finalName = siteData.name.trim();
    if (!finalName) {
      if (suggestedNames.length === 0) {
        const names = generateNameSuggestions(siteData.type, siteData.industry);
        finalName = names[0] || 'My Website';
      } else {
        finalName = suggestedNames[0];
      }
      updateSiteData('name', finalName);
    }

    try {
      const response = await api.post('/websites/generate', {
        ...siteData,
        name: finalName,
        ...(user?.id != null ? { userId: user.id } : {}),
      });

      if (response.data?.success) {
        setGeneratedSite(response.data.data);
        setCurrentStep(5);
      } else {
        throw new Error(response.data?.message || 'Failed to generate site');
      }
    } catch (err: unknown) {
      console.error('Generate site failed:', err);
      const fallbackSite = generateLocalSite();
      setGeneratedSite(fallbackSite);
      setCurrentStep(5);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateLocalSite = (): GeneratedSite => {
    const colorScheme = colorSchemes.find((c) => c.id === siteData.colorScheme);
    const gradientClass = colorScheme?.gradient || 'from-blue-600 to-blue-800';

    const sections: string[] = [];

    if (siteData.features.includes('hero')) {
      sections.push(`
        <section class="relative py-24 bg-gradient-to-br ${gradientClass}">
          <div class="container mx-auto px-6 text-center">
            <h1 class="text-5xl md:text-6xl font-bold text-white mb-6">${siteData.name}</h1>
            <p class="text-xl text-white/90 mb-8 max-w-2xl mx-auto">${siteData.description || 'Welcome to our website. We help businesses succeed.'}</p>
            <div class="flex gap-4 justify-center flex-wrap">
              <a href="#contact" class="px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors">Get Started</a>
              <a href="#about" class="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors">Learn More</a>
            </div>
          </div>
        </section>
      `);
    }

    if (siteData.features.includes('about')) {
      sections.push(`
        <section id="about" class="py-20 bg-white">
          <div class="container mx-auto px-6">
            <div class="max-w-3xl mx-auto text-center mb-16">
              <h2 class="text-4xl font-bold text-gray-900 mb-4">About ${siteData.name}</h2>
              <p class="text-lg text-gray-600">${siteData.description || 'We are dedicated to providing exceptional service and solutions.'}</p>
            </div>
            <div class="grid md:grid-cols-3 gap-8">
              <div class="text-center p-6"><div class="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center"><span class="text-3xl">🎯</span></div><h3 class="text-xl font-semibold mb-2">Our Mission</h3><p class="text-gray-600">To deliver excellence in everything we do.</p></div>
              <div class="text-center p-6"><div class="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center"><span class="text-3xl">💡</span></div><h3 class="text-xl font-semibold mb-2">Our Vision</h3><p class="text-gray-600">To be the leading provider in our industry.</p></div>
              <div class="text-center p-6"><div class="w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center"><span class="text-3xl">⭐</span></div><h3 class="text-xl font-semibold mb-2">Our Values</h3><p class="text-gray-600">Integrity, innovation, and customer focus.</p></div>
            </div>
          </div>
        </section>
      `);
    }

    if (siteData.features.includes('services')) {
      sections.push(`
        <section id="services" class="py-20 bg-gray-50">
          <div class="container mx-auto px-6">
            <div class="text-center mb-16"><h2 class="text-4xl font-bold text-gray-900 mb-4">Our Services</h2><p class="text-lg text-gray-600 max-w-2xl mx-auto">Comprehensive solutions tailored to your needs</p></div>
            <div class="grid md:grid-cols-3 gap-8">
              <div class="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow"><div class="w-12 h-12 bg-gradient-to-br ${gradientClass} rounded-lg flex items-center justify-center mb-4"><span class="text-2xl text-white">🚀</span></div><h3 class="text-xl font-semibold mb-3">Service One</h3><p class="text-gray-600 mb-4">Professional service that helps your business grow and succeed.</p><a href="#" class="text-blue-600 font-medium hover:underline">Learn more →</a></div>
              <div class="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow"><div class="w-12 h-12 bg-gradient-to-br ${gradientClass} rounded-lg flex items-center justify-center mb-4"><span class="text-2xl text-white">💼</span></div><h3 class="text-xl font-semibold mb-3">Service Two</h3><p class="text-gray-600 mb-4">Comprehensive solutions designed for modern businesses.</p><a href="#" class="text-blue-600 font-medium hover:underline">Learn more →</a></div>
              <div class="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow"><div class="w-12 h-12 bg-gradient-to-br ${gradientClass} rounded-lg flex items-center justify-center mb-4"><span class="text-2xl text-white">⚡</span></div><h3 class="text-xl font-semibold mb-3">Service Three</h3><p class="text-gray-600 mb-4">Fast, reliable service that delivers results.</p><a href="#" class="text-blue-600 font-medium hover:underline">Learn more →</a></div>
            </div>
          </div>
        </section>
      `);
    }

    if (siteData.features.includes('testimonials')) {
      sections.push(`
        <section class="py-20 bg-white">
          <div class="container mx-auto px-6">
            <div class="text-center mb-16"><h2 class="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2><p class="text-lg text-gray-600">Trusted by businesses worldwide</p></div>
            <div class="grid md:grid-cols-3 gap-8">
              <div class="bg-gray-50 p-8 rounded-xl"><div class="flex items-center mb-4"><div class="w-12 h-12 bg-gradient-to-br ${gradientClass} rounded-full flex items-center justify-center text-white font-bold">JD</div><div class="ml-4"><h4 class="font-semibold">John Doe</h4><p class="text-sm text-gray-500">CEO, TechCorp</p></div></div><p class="text-gray-600 italic">"Exceptional service and results. Highly recommended!"</p><div class="flex mt-4 text-yellow-400">★★★★★</div></div>
              <div class="bg-gray-50 p-8 rounded-xl"><div class="flex items-center mb-4"><div class="w-12 h-12 bg-gradient-to-br ${gradientClass} rounded-full flex items-center justify-center text-white font-bold">JS</div><div class="ml-4"><h4 class="font-semibold">Jane Smith</h4><p class="text-sm text-gray-500">Founder, StartupXYZ</p></div></div><p class="text-gray-600 italic">"Transformed our business completely. Amazing team!"</p><div class="flex mt-4 text-yellow-400">★★★★★</div></div>
              <div class="bg-gray-50 p-8 rounded-xl"><div class="flex items-center mb-4"><div class="w-12 h-12 bg-gradient-to-br ${gradientClass} rounded-full flex items-center justify-center text-white font-bold">MJ</div><div class="ml-4"><h4 class="font-semibold">Mike Johnson</h4><p class="text-sm text-gray-500">Director, Enterprise Inc</p></div></div><p class="text-gray-600 italic">"Professional, efficient, and excellent results."</p><div class="flex mt-4 text-yellow-400">★★★★★</div></div>
            </div>
          </div>
        </section>
      `);
    }

    if (siteData.features.includes('cta')) {
      sections.push(`
        <section class="py-20 bg-gradient-to-br ${gradientClass}">
          <div class="container mx-auto px-6 text-center">
            <h2 class="text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p class="text-xl text-white/90 mb-8 max-w-2xl mx-auto">Join thousands of satisfied customers and take your business to the next level.</p>
            <a href="#contact" class="inline-block px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors">Contact Us Today</a>
          </div>
        </section>
      `);
    }

    if (siteData.features.includes('contact')) {
      sections.push(`
        <section id="contact" class="py-20 bg-white">
          <div class="container mx-auto px-6">
            <div class="max-w-4xl mx-auto">
              <div class="text-center mb-12"><h2 class="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2><p class="text-lg text-gray-600">We'd love to hear from you</p></div>
              <div class="grid md:grid-cols-2 gap-12">
                <div><h3 class="text-xl font-semibold mb-4">Contact Information</h3><div class="space-y-4"><p class="flex items-center gap-3 text-gray-600"><span class="text-xl">📍</span> 123 Business Street, City, State 12345</p><p class="flex items-center gap-3 text-gray-600"><span class="text-xl">📧</span> hello@${siteData.name.toLowerCase().replace(/\s+/g, '')}.com</p><p class="flex items-center gap-3 text-gray-600"><span class="text-xl">📞</span> (555) 123-4567</p></div></div>
                <form class="space-y-4"><div><input type="text" placeholder="Your Name" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"></div><div><input type="email" placeholder="Your Email" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"></div><div><textarea rows="4" placeholder="Your Message" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"></textarea></div><button type="submit" class="w-full px-8 py-4 bg-gradient-to-r ${gradientClass} text-white font-semibold rounded-lg hover:opacity-90 transition-opacity">Send Message</button></form>
              </div>
            </div>
          </div>
        </section>
      `);
    }

    if (siteData.features.includes('footer')) {
      sections.push(`
        <footer class="py-12 bg-gray-900 text-white">
          <div class="container mx-auto px-6">
            <div class="grid md:grid-cols-4 gap-8 mb-8">
              <div><h3 class="text-xl font-bold mb-4">${siteData.name}</h3><p class="text-gray-400">${siteData.description || 'Building the future, one step at a time.'}</p></div>
              <div><h4 class="font-semibold mb-4">Quick Links</h4><ul class="space-y-2 text-gray-400"><li><a href="#" class="hover:text-white transition-colors">Home</a></li><li><a href="#about" class="hover:text-white transition-colors">About</a></li><li><a href="#services" class="hover:text-white transition-colors">Services</a></li><li><a href="#contact" class="hover:text-white transition-colors">Contact</a></li></ul></div>
              <div><h4 class="font-semibold mb-4">Services</h4><ul class="space-y-2 text-gray-400"><li><a href="#" class="hover:text-white transition-colors">Service One</a></li><li><a href="#" class="hover:text-white transition-colors">Service Two</a></li><li><a href="#" class="hover:text-white transition-colors">Service Three</a></li></ul></div>
              <div><h4 class="font-semibold mb-4">Follow Us</h4><div class="flex gap-4"><a href="#" class="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">📘</a><a href="#" class="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">🐦</a><a href="#" class="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">📸</a><a href="#" class="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">💼</a></div></div>
            </div>
            <div class="pt-8 border-t border-gray-800 text-center text-gray-400"><p>© ${new Date().getFullYear()} ${siteData.name}. All rights reserved.</p></div>
          </div>
        </footer>
      `);
    }

    const nav = `
      <nav class="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur shadow-sm z-50">
        <div class="container mx-auto px-6 py-4">
          <div class="flex justify-between items-center">
            <a href="#" class="text-2xl font-bold bg-gradient-to-r ${gradientClass} bg-clip-text text-transparent">${siteData.name}</a>
            <div class="hidden md:flex items-center gap-6">
              ${siteData.features.includes('about') ? '<a href="#about" class="text-gray-600 hover:text-gray-900 transition-colors">About</a>' : ''}
              ${siteData.features.includes('services') ? '<a href="#services" class="text-gray-600 hover:text-gray-900 transition-colors">Services</a>' : ''}
              ${siteData.features.includes('contact') ? '<a href="#contact" class="px-6 py-2 bg-gradient-to-r ' + gradientClass + ' text-white rounded-lg hover:opacity-90 transition-opacity">Contact</a>' : ''}
            </div>
          </div>
        </div>
      </nav>
    `;

    const html = `
      <div class="min-h-screen bg-white">
        ${nav}
        <div class="pt-16">
          ${sections.join('\n')}
        </div>
      </div>
    `;

    return {
      html: html.trim(),
      css: '',
      siteName: siteData.name,
      siteType: siteData.type,
      siteIndustry: siteData.industry,
      colorScheme: siteData.colorScheme,
      style: siteData.style,
      features: siteData.features,
    };
  };

  const handleEditSite = (): void => {
    if (generatedSite) {
      sessionStorage.setItem('ai_generated_site', JSON.stringify(generatedSite));
      navigate('/admin/website-builder/new?source=ai');
    }
  };

  const handleRegenerate = (): void => {
    setGeneratedSite(null);
    setCurrentStep(4);
  };

  const handleStartFresh = (): void => {
    navigate('/admin/website-builder/new');
  };

  const steps: StepInfo[] = [
    { num: 1, name: 'Basics', icon: BuildingOfficeIcon },
    { num: 2, name: 'Industry', icon: DocumentTextIcon },
    { num: 3, name: 'Design', icon: PaintBrushIcon },
    { num: 4, name: 'Features', icon: Cog6ToothIcon },
    { num: 5, name: 'Review', icon: CheckIcon },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="bg-white/5 backdrop-blur border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">AI Site Builder</h1>
                <p className="text-sm text-white/60">Create your website in minutes</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/website-pages')}
              className="text-white/60 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-12">
          {steps.map((step, idx) => (
            <div key={step.num} className="flex items-center">
              <div className={`flex items-center gap-2 ${
                currentStep >= step.num ? 'text-white' : 'text-white/40'
              }`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  currentStep > step.num ? 'bg-green-500' :
                  currentStep === step.num ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
                  'bg-white/10'
                }`}>
                  {currentStep > step.num ? (
                    <CheckIcon className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className="hidden sm:block text-sm font-medium">{step.name}</span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-12 lg:w-24 h-0.5 mx-2 ${
                  currentStep > step.num ? 'bg-green-500' : 'bg-white/10'
                }`} />
              )}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {currentStep === 1 && (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Let&apos;s start with the basics</h2>
              <p className="text-gray-600 mb-8">Tell us about your website</p>

              <div className="space-y-6">
                <div>
                  <label htmlFor="aisite-builder-name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Site Name <span className="text-gray-400 font-normal">(optional - AI can suggest)</span>
                  </label>
                  <input id="aisite-builder-name"
                    type="text"
                    value={siteData.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      updateSiteData('name', e.target.value);
                      if (!e.target.value.trim()) {
                        setShowNameSuggestions(suggestedNames.length > 0);
                      } else {
                        setShowNameSuggestions(false);
                      }
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Leave blank for AI suggestions, or enter your name"
                  />
                  {!siteData.name.trim() && siteData.type && (
                    <p className="mt-2 text-sm text-purple-600 flex items-center gap-1">
                      <SparklesIcon className="w-4 h-4" />
                      AI will suggest names based on your selections
                    </p>
                  )}
                </div>

                <div>
                  <div className="field-caption block text-sm font-semibold text-gray-700 mb-3">
                    What type of site? <span className="text-red-500">*</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {siteTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => handleTypeChange(type.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          siteData.type === type.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-2xl mb-2 block">{type.icon}</span>
                        <span className="font-semibold text-gray-900 block">{type.name}</span>
                        <span className="text-xs text-gray-500">{type.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="aisite-builder-description" className="block text-sm font-semibold text-gray-700 mb-2">Brief Description</label>
                  <textarea id="aisite-builder-description"
                    value={siteData.description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateSiteData('description', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Tell us what your business does, your mission, or what you want to highlight..."
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">What industry are you in?</h2>
              <p className="text-gray-600 mb-8">This helps us customize your site content and suggest names</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {industries.map((industry) => (
                  <button
                    key={industry}
                    onClick={() => handleIndustryChange(industry)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      siteData.industry === industry
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="font-medium text-gray-900">{industry}</span>
                  </button>
                ))}
              </div>

              {!siteData.name.trim() && siteData.industry && suggestedNames.length > 0 && (
                <div className="mt-8 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <SparklesIcon className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-gray-900">AI-Suggested Names</h3>
                    </div>
                    <button
                      onClick={refreshSuggestions}
                      className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                      Refresh
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Click a name to use it, or continue without a name and we&apos;ll use the best suggestion.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {suggestedNames.map((name, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectSuggestedName(name)}
                        className="p-3 bg-white rounded-lg border border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all text-left"
                      >
                        <span className="font-medium text-gray-900">{name}</span>
                        <span className="block text-xs text-gray-500 mt-1">
                          {name.toLowerCase().replace(/\s+/g, '')}.com
                        </span>
                      </button>
                    ))}
                  </div>
                  {siteData.name && (
                    <div className="mt-4 p-3 bg-white rounded-lg border border-green-200">
                      <p className="text-sm text-green-700 flex items-center gap-2">
                        <CheckIcon className="w-4 h-4" />
                        Selected: <strong>{siteData.name}</strong>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {currentStep === 3 && (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose your design</h2>
              <p className="text-gray-600 mb-8">Pick colors and style that match your brand</p>

              <div className="space-y-8">
                <div>
                  <div className="field-caption block text-sm font-semibold text-gray-700 mb-3">Color Scheme</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {colorSchemes.map((scheme) => (
                      <button
                        key={scheme.id}
                        onClick={() => updateSiteData('colorScheme', scheme.id)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          siteData.colorScheme === scheme.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex gap-1 mb-2">
                          {scheme.colors.map((color, idx) => (
                            <div
                              key={idx}
                              className="w-6 h-6 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <span className="font-medium text-gray-900 text-sm">{scheme.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="field-caption block text-sm font-semibold text-gray-700 mb-3">Design Style</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {designStyles.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => updateSiteData('style', style.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          siteData.style === style.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-semibold text-gray-900 block">{style.name}</span>
                        <span className="text-xs text-gray-500">{style.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Select your sections</h2>
              <p className="text-gray-600 mb-8">Choose what to include on your site</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {availableFeatures.map((feature) => (
                  <button
                    key={feature.id}
                    onClick={() => toggleFeature(feature.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      siteData.features.includes(feature.id)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-900">{feature.name}</span>
                      {siteData.features.includes(feature.id) && (
                        <CheckIcon className="w-5 h-5 text-purple-600" />
                      )}
                    </div>
                    <span className="text-xs text-gray-500">{feature.description}</span>
                  </button>
                ))}
              </div>

              <div className="mt-6 p-4 bg-purple-50 rounded-xl">
                <p className="text-sm text-purple-800">
                  <strong>Selected:</strong> {siteData.features.length} sections
                </p>
              </div>
            </div>
          )}

          {currentStep === 5 && generatedSite && (
            <div className="p-8">
              <div className="page-header mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Your site is ready!</h2>
                  <p className="text-gray-600">Review your site and start editing</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleRegenerate}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                    Regenerate
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <GlobeAltIcon className="w-5 h-5 text-gray-400" />
                    <span className="font-semibold text-gray-900">{generatedSite.siteName}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {siteTypes.find((t) => t.id === generatedSite.siteType)?.name || 'Website'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SwatchIcon className="w-5 h-5 text-gray-400" />
                    <span className="font-semibold text-gray-900">
                      {colorSchemes.find((c) => c.id === generatedSite.colorScheme)?.name}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {designStyles.find((s) => s.id === generatedSite.style)?.name} style
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                    <span className="font-semibold text-gray-900">{generatedSite.features?.length || 0} Sections</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">
                    {generatedSite.features?.slice(0, 3).join(', ')}...
                  </p>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
                <div className="bg-gray-100 px-4 py-2 border-b flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <span className="text-sm text-gray-500 ml-2">Preview</span>
                </div>
                <div className="h-96 overflow-auto">
                  <iframe
                    srcDoc={`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
                          <style>body { margin: 0; }</style>
                        </head>
                        <body>${generatedSite.html}</body>
                      </html>
                    `}
                    className="w-full h-full border-0"
                    title="Site Preview"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleEditSite}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
                >
                  <PencilIcon className="w-5 h-5" />
                  Edit Your Site
                </button>
                <button
                  onClick={handleStartFresh}
                  className="px-6 py-4 border border-gray-300 rounded-xl hover:bg-gray-50 font-medium"
                >
                  Start Fresh
                </button>
              </div>
            </div>
          )}

          {isGenerating && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-6 relative">
                <div className="absolute inset-0 rounded-full border-4 border-purple-200" />
                <div className="absolute inset-0 rounded-full border-4 border-purple-600 border-t-transparent animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Creating your site...</h2>
              <p className="text-gray-600">Our AI is designing your perfect website</p>
            </div>
          )}

          {currentStep < 5 && !isGenerating && (
            <div className="px-8 py-4 bg-gray-50 border-t flex items-center justify-between">
              <button
                onClick={() => setCurrentStep((prev) => Math.max(1, prev - 1))}
                disabled={currentStep === 1}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back
              </button>

              {currentStep < 4 ? (
                <button
                  onClick={() => setCurrentStep((prev) => prev + 1)}
                  disabled={!canProceed()}
                  className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={generateSite}
                  disabled={!canProceed()}
                  className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SparklesIcon className="w-4 h-4" />
                  Generate Site
                </button>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={handleStartFresh}
            className="text-white/60 hover:text-white transition-colors text-sm"
          >
            Skip wizard and start with blank editor →
          </button>
        </div>
      </div>
    </div>
  );
}

export default AISiteBuilder;
